import { useContext } from 'react';
import AppContext from '../../../AppContext';
import BookOfBusinessRowData from './BookOfBusinessRowData';
import styles from './BookOfBusinessRow.module.css';
import defStyles from './DeficiencyByLobRow.module.css';
import { RatioFormat, DollarFormat } from '../../format';
import { DataResult } from '../../model/DataResult';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return styles.indent;
    case 'TOTAL':
      return styles.total;
    default:
      return '';
  }
}

function hasAnyOutsideBenchmarks(
  percentOrPointChange: DataResult,
  dollarChange: DataResult,
  currentYearPremium: DataResult,
  currentYearPercentOfTotal: DataResult,
  currentYearPDLR: DataResult,
  priorYearPremium: DataResult,
  priorYearPercentOfTotal: DataResult,
  priorYear2Premium: DataResult,
  priorYear2PercentOfTotal: DataResult,
  priorYearPDLR: DataResult
) {
  return (
    percentOrPointChange?.outsideBenchmark ||
    dollarChange?.outsideBenchmark ||
    currentYearPremium?.outsideBenchmark ||
    currentYearPercentOfTotal?.outsideBenchmark ||
    currentYearPDLR?.outsideBenchmark ||
    priorYearPremium?.outsideBenchmark ||
    priorYearPercentOfTotal?.outsideBenchmark ||
    priorYear2Premium?.outsideBenchmark ||
    priorYear2PercentOfTotal?.outsideBenchmark ||
    priorYearPDLR?.outsideBenchmark
  );
}

export default function BookOfBusinessRow({
  sectionKey,
  subSectionKey,
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  percentOrPointChange,
  dollarChange,
  currentYearPremium,
  currentYearPercentOfTotal,
  currentYearPDLR,
  priorYearPremium,
  priorYearPercentOfTotal,
  priorYear2Premium,
  priorYear2PercentOfTotal,
  priorYearPDLR,
  calculateDetail,
  rowDataSource,
  rowDisplay
}: Readonly<BookOfBusinessRowData>) {
  const {
    showBenchmark,
    riskRepository
  }: { readonly showBenchmark: boolean; riskRepository: string } = useContext(AppContext);

  if (!label || label.trim().toLowerCase() === 'missing') {
    return null;
  }

  if (rowType === 'LABEL') {
    const id =
      'label-' +
      label
        .replace(/\s+/g, '-')
        .replace(/[^a-z^A-Z^0-9^-]+/g, '')
        .toLowerCase();
    return (
      <div
        id={id}
        data-testid={id}
        className={`${styles.container} ${showBenchmark ? '' : styles.collapse} `}>
        <RowHelmet label={label} />
        <LabelRow></LabelRow>
      </div>
    );
  } else if (
    sectionKey === 'liabilities-analysis' ||
    sectionKey === 'revenues-and-expenses' ||
    sectionKey === 'affiliates'
  ) {
    const id = `${sectionKey}-${rowKey}`;
    const isGrey = getDataDisplayType(rowDisplay, riskRepository);
    return (
      <div
        id={id}
        data-testid={id}
        className={`${defStyles.container} ${showBenchmark ? '' : defStyles.collapse}  ${
          !isGrey ? '' : defStyles.grey
        }`}>
        <RowHelmet
          label={label}
          isOutsideBenchmark={hasAnyOutsideBenchmarks(
            percentOrPointChange,
            dollarChange,
            currentYearPremium,
            currentYearPercentOfTotal,
            currentYearPDLR,
            priorYearPremium,
            priorYearPercentOfTotal,
            priorYear2Premium,
            priorYear2PercentOfTotal,
            priorYearPDLR
          )}
          benchmarkText={benchmark}
          calculateDetail={calculateDetail}
          rowDataSource={rowDataSource}
          rowKey={rowKey}
          rowType={rowType}
          sectionKey={sectionKey}
          subSectionKey={subSectionKey}
        />
        <DollarFormat {...currentYearPremium} />
        <RatioFormat {...currentYearPercentOfTotal} />
        <DollarFormat {...priorYearPremium} />
        <RatioFormat {...priorYearPercentOfTotal} />
        <DollarFormat {...priorYear2Premium} />
        <RatioFormat {...priorYear2PercentOfTotal} />
      </div>
    );
  } else {
    const id = `${sectionKey}-${rowKey}`;
    return (
      <div
        id={id}
        data-testid={id}
        className={`${styles.container} ${showBenchmark ? '' : styles.collapse} ${rowClassSwitch(
          rowClass
        )}`}>
        <RowHelmet
          label={label}
          isOutsideBenchmark={hasAnyOutsideBenchmarks(
            percentOrPointChange,
            dollarChange,
            currentYearPremium,
            currentYearPercentOfTotal,
            currentYearPDLR,
            priorYearPremium,
            priorYearPercentOfTotal,
            priorYear2Premium,
            priorYear2PercentOfTotal,
            priorYearPDLR
          )}
          benchmarkText={benchmark}
          calculateDetail={calculateDetail}
          rowDataSource={rowDataSource}
          rowKey={rowKey}
          rowType={rowType}
          sectionKey={sectionKey}
          subSectionKey={subSectionKey}
        />

        <RatioFormat {...percentOrPointChange} showDefaultValue={true} />
        <DollarFormat {...dollarChange} showDefaultValue={true} />
        <p></p>
        <p></p>
        <DollarFormat {...currentYearPremium} />
        <RatioFormat {...currentYearPercentOfTotal} />
        <RatioFormat {...currentYearPDLR} />
        <DollarFormat {...priorYearPremium} />
        <RatioFormat {...priorYearPercentOfTotal} />
        <RatioFormat {...priorYearPDLR} />
      </div>
    );
  }
}

function getDataDisplayType(
  rowDisplay:
    | {
        cr: string;
        lg: string;
        lq: string;
        mk: string;
        op: string;
        pu: string;
        rp: string;
        rv: string;
        st: string;
        fs: string;
      }
    | undefined,
  riskRepository: any
) {
  if (riskRepository === 'FR' || !rowDisplay) {
    return false;
  }
  if (
    riskRepository &&
    rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay] === 'GREY'
  ) {
    return true;
  }
}
